/**
 * Hoppscotch collection export → NormalizedCollection[].
 *
 * Shapes read from `hoppscotch/hoppscotch` packages/hoppscotch-data (27.09.2026):
 *   collection { v: 1..12, name, folders[] (same shape), requests[], auth, headers[{key,value,active}],
 *                variables[{key,initialValue,currentValue,secret}] (v10+), description (v11+),
 *                preRequestScript, testScript (v12) }  — one object, or an ARRAY of them
 *   REST request { v: "1".."17", name, method, endpoint, params[], headers[], preRequestScript,
 *                testScript, auth{authType, authActive, …}, body{contentType, body},
 *                requestVariables[] (v2+), description (v17), responses{} }
 *   legacy v0 request { url, path, auth:'Bearer Token', bearerToken, httpUser, httpPassword,
 *                contentType, body }
 *   GraphQL request { name, url, headers, query, variables, auth }  (no endpoint / method)
 *
 * It goes through the same IR and mapping as Bruno (`bruno/toNormalized.js`),
 * so folders, auth, scripts-on-settings and COL-01 behave identically.
 */
import { convertHoppVars as cv } from './hoppVars';
import { emptyDefaults, emptyCounts, row } from '../bruno/ir';
import { irToNormalized } from '../bruno/toNormalized';

const arr = (v) => (Array.isArray(v) ? v : []);
const str = (v) => (v == null ? '' : String(v));

const kvRows = (items) =>
  arr(items)
    .filter((h) => h && h.key != null)
    .map((h) => row(cv(str(h.key)), cv(str(h.value)), h.active !== false));

/** Hoppscotch's raw key-value text (`key: value` lines, `#` = disabled — rawKeyValue.ts). */
export function parseRawKeyValue(text) {
  const rows = [];
  for (const rawLine of str(text).split(/\r?\n/)) {
    let line = rawLine.trim();
    if (!line) continue;
    const disabled = line.startsWith('#');
    if (disabled) line = line.slice(1).trim();
    let key;
    let rest;
    const quoted = line.match(/^"((?:[^"\\]|\\.)*)"\s*:(.*)$/);
    if (quoted) {
      try { key = JSON.parse(`"${quoted[1]}"`); } catch { key = quoted[1]; }
      rest = quoted[2];
    } else {
      const colon = line.indexOf(':');
      if (colon === -1) continue;
      key = line.slice(0, colon).trim();
      rest = line.slice(colon + 1);
    }
    let value = rest.trim();
    const qv = value.match(/^"((?:[^"\\]|\\.)*)"$/);
    if (qv) {
      try { value = JSON.parse(`"${qv[1]}"`); } catch { value = qv[1]; }
    }
    if (key) rows.push({ key, value, enabled: !disabled });
  }
  return rows;
}

function readBody(body) {
  const contentType = str(body?.contentType).toLowerCase();
  const raw = body?.body;
  if (!contentType) return { mode: 'none' };
  if (contentType === 'application/x-www-form-urlencoded') {
    const rows = Array.isArray(raw)
      ? raw.filter((r) => r && r.key != null).map((r) => ({ key: str(r.key), value: str(r.value), enabled: r.active !== false }))
      : parseRawKeyValue(raw);
    return { mode: 'urlencoded', rows: rows.map((r) => ({ key: cv(r.key), value: cv(r.value), enabled: r.enabled })) };
  }
  if (contentType === 'multipart/form-data') {
    return {
      mode: 'multipart',
      rows: arr(raw)
        .filter((r) => r && r.key != null)
        .map((r) => {
          const enabled = r.active !== false;
          if (r.isFile) {
            // Exported file rows carry no usable path (Blobs serialise to {}); a
            // string, if present, is at most a hint (COL-01 in mapPostmanBody).
            const src = typeof r.value === 'string' ? r.value : '';
            return { key: cv(str(r.key)), value: '', enabled, type: 'file', src };
          }
          return { key: cv(str(r.key)), value: cv(str(r.value)), enabled, type: 'text' };
        }),
    };
  }
  if (contentType === 'application/octet-stream') return { mode: 'file' };
  const text = cv(typeof raw === 'string' ? raw : raw == null ? '' : JSON.stringify(raw));
  if (contentType.includes('json')) return { mode: 'json', text };
  if (contentType.includes('xml')) return { mode: 'xml', text };
  return { mode: 'text', text };
}

function readOAuth2(auth) {
  const info = auth.grantTypeInfo || {};
  const grantRaw = str(info.grantType).toUpperCase();
  const grant = {
    AUTHORIZATION_CODE: 'authorization_code',
    CLIENT_CREDENTIALS: 'client_credentials',
    PASSWORD: 'password',
    IMPLICIT: 'implicit',
  }[grantRaw] || (auth.authURL ? 'authorization_code' : 'implicit');
  return {
    mode: 'oauth2',
    oauth2: {
      grant,
      authUrl: cv(str(info.authEndpoint ?? auth.authURL)),
      // CLIENT_CREDENTIALS keeps the TOKEN url in `authEndpoint` (there is no tokenEndpoint field).
      tokenUrl: cv(str(info.tokenEndpoint ?? (grant === 'client_credentials' ? info.authEndpoint : auth.accessTokenURL))),
      callbackUrl: '',
      clientId: cv(str(info.clientID ?? auth.clientID)),
      clientSecret: cv(str(info.clientSecret)),
      scope: cv(str(info.scopes ?? auth.scope)),
      pkce: info.isPKCE === true,
      accessToken: cv(str(info.token ?? auth.token)),
    },
  };
}

/** Hoppscotch auth → IR auth. `authActive:false` disables it. */
function readAuth(auth, legacy) {
  if (typeof auth === 'string') {
    const src = legacy || {};
    const label = str(auth).toLowerCase();
    if (label === 'basic auth') return { mode: 'basic', username: cv(str(src.httpUser)), password: cv(str(src.httpPassword)) };
    if (label === 'bearer token') return { mode: 'bearer', token: cv(str(src.bearerToken)) };
    if (label === 'oauth 2.0') return { mode: 'bearer', token: cv(str(src.oauth2Token)) };
    return { mode: 'none' };
  }
  if (!auth || typeof auth !== 'object') return null;
  if (auth.authActive === false) return { mode: 'none' };
  const type = str(auth.authType).toLowerCase();
  switch (type) {
    case 'none':
    case 'inherit':
      return { mode: type };
    case 'basic':
      return { mode: 'basic', username: cv(str(auth.username)), password: cv(str(auth.password)) };
    case 'bearer':
      return { mode: 'bearer', token: cv(str(auth.token)) };
    case 'api-key': {
      const addTo = str(auth.addTo).toUpperCase().replace(/\s+/g, '_');
      return { mode: 'apikey', key: cv(str(auth.key)), value: cv(str(auth.value)), in: addTo === 'QUERY_PARAMS' ? 'query' : 'header' };
    }
    case 'oauth-2':
      return readOAuth2(auth);
    case 'aws-signature':
      return {
        mode: 'awsv4',
        accessKeyId: cv(str(auth.accessKey)),
        secretAccessKey: cv(str(auth.secretKey)),
        sessionToken: cv(str(auth.serviceToken)),
        service: cv(str(auth.serviceName)),
        region: cv(str(auth.region)),
      };
    default:
      return { mode: 'unsupported', unsupportedName: type || 'unknown' };
  }
}

const isGraphql = (r) => typeof r.query === 'string' && r.endpoint === undefined && r.method === undefined;

function requestToNode(r, seq) {
  const name = str(r.name) || 'Untitled Request';
  const counts = {
    ...emptyCounts(),
    vars: arr(r.requestVariables).length,
    examples: r.responses && typeof r.responses === 'object' ? Object.keys(r.responses).length : 0,
  };
  const scripts = { pre: str(r.preRequestScript).trim(), post: '', tests: str(r.testScript).trim() };
  if (isGraphql(r)) {
    return {
      kind: 'request', name, seq, reqType: 'graphql', method: 'POST', url: cv(str(r.url)),
      headers: kvRows(r.headers), query: [], pathParams: [],
      body: { mode: 'graphql', graphql: { query: str(r.query), variables: cv(str(r.variables)) } },
      auth: readAuth(r.auth, null) || { mode: 'inherit' },
      scripts, docs: str(r.description), counts,
    };
  }
  const legacy = r.endpoint === undefined;
  const endpoint = legacy ? `${str(r.url)}${str(r.path)}` : str(r.endpoint);
  const body = legacy
    ? readBody({ contentType: r.contentType, body: r.bodyParams && arr(r.bodyParams).length ? r.bodyParams : r.body })
    : readBody(r.body);
  return {
    kind: 'request',
    name,
    seq,
    reqType: 'http',
    method: str(r.method || 'GET').toUpperCase(),
    url: cv(endpoint),
    headers: kvRows(r.headers),
    query: kvRows(r.params),
    pathParams: [],
    body: legacy && !str(r.contentType) && !str(r.body) ? { mode: 'none' } : body,
    auth: readAuth(r.auth, r) || { mode: 'inherit' },
    scripts,
    docs: str(r.description),
    counts,
  };
}

function readDefaults(col, stats) {
  const d = emptyDefaults();
  d.headers = kvRows(col.headers);
  d.auth = readAuth(col.auth, null);
  d.vars = arr(col.variables)
    .filter((v) => v && v.key != null && String(v.key) !== '')
    .map((v) => {
      if (v.secret === true) stats.secretVars += 1;
      const value = v.secret === true ? '' : (v.initialValue || v.currentValue || v.value || '');
      return row(cv(str(v.key)), cv(str(value)), true);
    });
  d.scripts = { pre: str(col.preRequestScript).trim(), post: '', tests: str(col.testScript).trim() };
  return d;
}

function collectionToNodes(col, stats) {
  const folders = arr(col.folders).filter((f) => f && typeof f === 'object');
  const requests = arr(col.requests).filter((r) => r && typeof r === 'object');
  return [
    ...folders.map((f, i) => ({
      kind: 'folder',
      name: str(f.name) || 'Folder',
      seq: i,
      docs: str(f.description),
      defaults: readDefaults(f, stats),
      children: collectionToNodes(f, stats),
    })),
    ...requests.map((r, i) => requestToNode(r, folders.length + i)),
  ];
}

/**
 * @param {object|object[]} data One collection or an array of them
 * @param {string[]} warnings
 * @returns {import('../types').NormalizedCollection[]}
 */
export function parseHoppscotch(data, warnings) {
  const roots = (Array.isArray(data) ? data : [data]).filter((c) => c && typeof c === 'object');
  const stats = { secretVars: 0 };
  const collections = roots.map((col) =>
    irToNormalized(
      {
        name: str(col.name) || 'Hoppscotch Collection',
        docs: str(col.description),
        defaults: readDefaults(col, stats),
        children: collectionToNodes(col, stats),
        environments: [],
      },
      warnings,
      { dialect: 'pw', sourceLabel: 'Hoppscotch' }
    )
  );
  if (stats.secretVars) {
    warnings.push(`${stats.secretVars} secret collection variable value${stats.secretVars === 1 ? ' was' : 's were'} not in the export; fill ${stats.secretVars === 1 ? 'it' : 'them'} in after import.`);
  }
  return collections;
}
